// REACT
import { useDispatch } from "react-redux";
import { useEffect } from "react";

// MATERIAL-UI
import { Grid } from "@mui/material";
import { styled } from "@mui/system";

// COMPONENTS
import ContactChangeButton, {
  ContactChangeStyledButton,
} from "src/components/settings/contactChange/ContactChangeButton";

// ASSETS
import { BodyStyles } from "./index.styles";
import PersonalDetails from "../personal-details";
import { BodyProps } from "./types";
import Preloader from "src/utils/Preloader";
import { setUserEmail, setUserPhone } from "src/store/features/contactChange.slice";
import { LoadingDotsWithText } from "src/components/dca/styled-components/LoadingDots";
import api from "src/store/features/api";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import usePermission from "src/utils/hooks/usePermission";

const StyledRow = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "8px 0px",
  alignItems: "center",
  gap: "8px",
}));

const Body = (props: BodyProps) => {
  const classes = BodyStyles();
  const dispatch = useDispatch();
  const { isGuest } = usePermission();
  const [generateSecureUploadLink, { isFetching }] = api.useLazyCreateDynamicFileUploadLinkQuery();

  useEffect(() => {
    if (props.data[1]?.[1] && props.isUser) {
      dispatch(setUserEmail(props.data[1][1]));
    }
    if (props.data[2]?.[1] && props.isUser) {
      dispatch(setUserPhone(props.data[2][1]));
    }
  }, [props.data[1]?.[1], props.data[2]?.[1], props.isUser]);

  return (
    <Grid container className={classes.container}>
      {!props.data || !props.data[0]?.[1] || !props.data[1]?.[1] ? (
        // Run preloader if data hasn't been fetched
        <Preloader />
      ) : (
        <>
          {/* FULL NAME */}
          <PersonalDetails
            email={props.data[1][1]}
            label={props.data[0][0]}
            content={props.data[0][1]}
            isUser={props.isUser}
          />

          {/* EMAIL ADDRESS */}
          <StyledRow>
            <PersonalDetails
              label={props.data[1][0]}
              content={props.data[1][1]}
              isUser={props.isUser}
            />
            {/* commented out for prod */}
            {props.isUser && <ContactChangeButton type="email" />}
          </StyledRow>

          {/* CLIENT PHONE NUMBER */}
          {props.data[2]?.[1] && (
            <StyledRow>
              <PersonalDetails
                label={props.data[2][0]}
                content={props.data[2][1]}
                isUser={props.isUser}
              />
              {props.isUser && <ContactChangeButton type="phone" />}
            </StyledRow>
          )}

          {/* BROKER PHONE NUMBER AU */}
          {props.data[3]?.[1] && (
            <PersonalDetails
              label={props.data[3][0]}
              content={props.data[3][1]}
              isUser={props.isUser}
            />
          )}

          {/* BROKER PHONE NUMBER US */}
          {props.data[4]?.[1] && (
            <PersonalDetails
              label={props.data[4][0]}
              content={props.data[4][1]}
              isUser={props.isUser}
            />
          )}

          {/* ADDRESS */}
          {props.data[5]?.[1] && (
            <StyledRow sx={{ width: "100%", justifyContent: "space-between" }}>
              <PersonalDetails
                label={props.data[5][0]}
                content={props.data[5][1]}
                isUser={props.isUser}
              />
              {props.isUser && !isGuest && (
                <BalanceTooltip
                  message={
                    "To change your address, please upload a proof of address document. You'll be redirected to the upload page by clicking here"
                  }
                  align="top-end">
                  <ContactChangeStyledButton
                    onClick={() => {
                      generateSecureUploadLink({ reason: "Proof of Address" })
                        .unwrap()
                        .then((response) => {
                          if (response.link) window.open(response.link, "_blank");
                        })
                        .catch((error) => {
                          console.error("Error generating upload URL:", error);
                        });
                    }}>
                    {isFetching ? <LoadingDotsWithText text="Please wait" /> : "Change Address"}
                  </ContactChangeStyledButton>
                </BalanceTooltip>
              )}
            </StyledRow>
          )}
        </>
      )}
    </Grid>
  );
};

export default Body;
